import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { technicalSpecs, submarineImages } from '../data/mock';
import { Gear } from './animations/Gear';
import SectionTitle from './SectionTitle';

export default function Specs() {
  return (
    <section id="specs" className="relative py-24 md:py-32 overflow-hidden">
      <div className="absolute inset-0 bg-grid opacity-50" />
      <div className="absolute -left-40 top-1/3 text-cyan-400/[0.04]">
        <div className="spin-rev"><Gear size={500} teeth={18} /></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <SectionTitle code="SECTION / 02" title="TEKNİK SPESİFİKASYONLAR" subtitle="Onaylanmış mühendislik verileri ve sistem parametreleri." />

        <div className="mt-16 grid lg:grid-cols-12 gap-8">
          {/* Schematic image */}
          <div className="lg:col-span-5 relative">
            <div className="relative aspect-[4/5] border border-cyan-500/20 overflow-hidden bg-[#080d15]">
              <span className="corner-mark corner-tl" />
              <span className="corner-mark corner-tr" />
              <span className="corner-mark corner-bl" />
              <span className="corner-mark corner-br" />
              <img
                src={submarineImages.exterior2}
                alt="Utku"
                className="absolute inset-0 w-full h-full object-cover opacity-70"
                style={{ filter: 'grayscale(0.4) contrast(1.1) hue-rotate(170deg)' }}
              />
              <div className="absolute inset-0" style={{
                background: 'linear-gradient(180deg, rgba(5,8,13,0.3) 0%, transparent 40%, rgba(5,8,13,0.85) 100%)'
              }} />
              <div className="absolute inset-0 bg-grid-fine opacity-30" />
              {/* Measurement marks */}
              <SVGSchematic />
              <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-[10px] font-mono tracking-[0.2em] text-cyan-300/70">
                <span>SCHEMATİC · SIDE PROFILE</span>
                <span>1:200 SCALE</span>
              </div>
              <div className="absolute top-4 left-4 text-[10px] font-mono text-cyan-400/80">
                <div>DWG-UTKU-001</div>
                <div className="text-cyan-400/50 mt-1">REV. 04 / 2026</div>
              </div>
            </div>
          </div>

          {/* Spec list */}
          <div className="lg:col-span-7">
            <div className="grid sm:grid-cols-2 gap-px bg-cyan-500/15 border border-cyan-500/15">
              {technicalSpecs.map((cat, i) => (
                <SpecCard key={cat.category} cat={cat} index={i} />
              ))}
            </div>

            <div className="mt-8 p-5 border border-cyan-500/20 bg-[#070b12] flex items-start gap-4">
              <div className="w-10 h-10 border border-cyan-400/40 flex items-center justify-center text-cyan-400 font-mono text-xs flex-shrink-0">i</div>
              <p className="text-sm text-slate-400 leading-relaxed">
                Tüm değerler nominal operasyon koşullarında alınmıştır. Savaş modunda performans parametreleri
                <span className="text-cyan-300"> %15-20 artış </span>
                gösterebilir. Görev raporları için yetkili komutanlık onayı gereklidir.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function SpecCard({ cat, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-50px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      className="relative bg-[#070b12] p-6 group hover:bg-[#0a1320] transition-colors"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-[11px] font-mono tracking-[0.25em] text-cyan-300">{cat.category}</h3>
        <span className="text-[10px] font-mono text-cyan-400/40">/ 0{index + 1}</span>
      </div>
      <div className="space-y-2">
        {cat.items.map((item, i) => (
          <div key={i} className="flex items-baseline gap-2 py-2 border-b border-dashed border-cyan-500/10 last:border-0">
            <span className="text-[11px] text-slate-500 flex-shrink-0">{item.name}</span>
            <span className="flex-1 border-b border-dotted border-cyan-500/15 mb-1" />
            <span className="font-mono text-sm text-white tabular-nums">{item.value}</span>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

function SVGSchematic() {
  return (
    <svg viewBox="0 0 400 500" className="absolute inset-0 w-full h-full pointer-events-none" preserveAspectRatio="xMidYMid slice">
      {/* Crosshair */}
      <line x1="200" y1="0" x2="200" y2="500" stroke="#00d4ff" strokeWidth="0.3" strokeDasharray="4 6" opacity="0.4" />
      <line x1="0" y1="250" x2="400" y2="250" stroke="#00d4ff" strokeWidth="0.3" strokeDasharray="4 6" opacity="0.4" />
      {/* Measurement leaders */}
      <g stroke="#00d4ff" strokeWidth="0.5" fill="none" opacity="0.6">
        <line x1="60" y1="180" x2="30" y2="180" />
        <line x1="30" y1="180" x2="30" y2="320" />
        <line x1="30" y1="320" x2="60" y2="320" />
      </g>
      <text x="35" y="253" fill="#00d4ff" fontSize="8" fontFamily="monospace" opacity="0.8">6.30 m</text>
      <g stroke="#00d4ff" strokeWidth="0.5" fill="none" opacity="0.6">
        <line x1="40" y1="430" x2="360" y2="430" />
        <line x1="40" y1="425" x2="40" y2="435" />
        <line x1="360" y1="425" x2="360" y2="435" />
      </g>
      <text x="170" y="450" fill="#00d4ff" fontSize="9" fontFamily="monospace" opacity="0.8">68.34 m</text>
      {/* Markers */}
      <circle cx="110" cy="230" r="3" fill="#00d4ff" />
      <text x="118" y="233" fill="#00d4ff" fontSize="7" fontFamily="monospace">A1 · PERİSCOPE</text>
      <circle cx="260" cy="260" r="3" fill="#00d4ff" />
      <text x="268" y="263" fill="#00d4ff" fontSize="7" fontFamily="monospace">B2 · ENGİNE</text>
      <circle cx="180" cy="290" r="3" fill="#00d4ff" />
      <text x="188" y="293" fill="#00d4ff" fontSize="7" fontFamily="monospace">C3 · SONAR</text>
    </svg>
  );
}
